import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { rootPath } from '@/common/utils/pathPrefix.util';
import { merge } from 'lodash-es';
import { AppMode } from '@/common/utils/getAppMode';
import initSelfService from '@/self-service/initSelfService';
import { initFederatedApp } from '@/self-service/initFederatedApp';
import type { AxiosHeaderValue } from 'axios';
import type { useAuthStore } from '@/common/store/pinia/auth';
import type { usePermissionsStore, AbilityRule } from '@/common/store/pinia/permissions';

export interface ModePreset {
  routerMode: 'history' | 'abstract';
  pageAfterLogin: string;
  featuresUrl: string;
  features: {
    s3: boolean;
  };
  layout: {
    header: boolean;
    sidebar: boolean;
  };
  abilityRules: AbilityRule[];
  initSelfService: ({
    authStore,
    permissionsStore,
    providers,
  }: {
    authStore: ReturnType<typeof useAuthStore>,
    permissionsStore: ReturnType<typeof usePermissionsStore>,
    providers?: AppOptions['providers']
  }) => Promise<void>;
  websocket: {
    wsUrl: string;
    vncWebSocketUrl: string;
    queryParams: Record<string, string | undefined>;
    headers: Record<string, AxiosHeaderValue | undefined>;
  };
}

const presets: Record<AppMode, ModePreset> = {
  default: {
    routerMode: 'history',
    pageAfterLogin: 'monitoring',
    featuresUrl: '/features.json',
    features: {
      s3: true,
    },
    layout: {
      header: true,
      sidebar: true,
    },
    abilityRules: [],
    initSelfService,
    websocket: {
      wsUrl: `${rootPath}/ws`,
      vncWebSocketUrl: '/websockify',
      queryParams: {},
      headers: {},
    }
  },
  federated: {
    routerMode: 'abstract',
    pageAfterLogin: 'computeServerInstances',
    featuresUrl: '/api/platform-web-assets/public/latest/ui-cyber-frame/features.json',
    features: {
      s3: false,
    },
    layout: {
      header: false,
      sidebar: false,
    },
    abilityRules: [
      {
        action: 'read',
        subject: 'compute_image_file',
        inverted: true
      }
    ],
    initSelfService: initFederatedApp,
    websocket: {
      wsUrl: '',
      vncWebSocketUrl: '',
      queryParams: {},
      headers: {},
    }
  },
};

const useAppModeStore = defineStore('appMode', () => {
  const mode = ref<AppMode>('default');

  const preset = computed<ModePreset>(() => presets[mode.value]);
  const pageAfterLogin = computed(() => preset.value.pageAfterLogin);
  const isFederated = computed(() => mode.value === 'federated');

  const isFeatureEnabled = (feature: keyof ModePreset['features']) => preset.value.features[feature];
  const isVisible = (element: keyof ModePreset['layout']) => preset.value.layout[element];
  const configure = (appMode: AppMode) => {
    mode.value = appMode;
  };
  const setSettings = (settings: Partial<ModePreset>) => {
    presets[mode.value] = merge(presets[mode.value], settings);
  };

  return {
    mode,
    preset,
    configure,
    pageAfterLogin,
    isFeatureEnabled,
    isVisible,
    isFederated,
    setSettings
  };
});

export default useAppModeStore;
